import { env } from '@/shared/config/env';
import { strings } from '@/shared/strings';
import { formatInstant } from './instant';

/**
 * The version a person reads: "v1.0.0 (5 Oct 2026)". The request of 5 October
 * 2026 (third), section 8.
 *
 * The release number and the day it was released, both from the newest entry
 * of src/shared/release/changelog.ts as the build baked them in. The sign in
 * screen, the profile and the menu call this with no arguments, so the three
 * always read the same; the update offer passes the release the server named.
 * A release with no day is the number alone.
 */
export function formatVersion(release: string = env.appRelease, day: string = env.appReleaseDate): string {
  return day === '' ? `v${release}` : `v${release} (${formatReleaseDay(day)})`;
}

/**
 * A calendar day, `2026-10-05`, as "5 Oct 2026".
 *
 * By the parts of the string and the month names in the strings module, never
 * through a Date or Intl: a release day is a day on the lab's calendar with no
 * clock and no zone to shift it, and the browser's own short month for
 * September is "Sept". Anything that is not such a day is shown as it is.
 */
export function formatReleaseDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return day;
  const [, year = '', month = '', date = ''] = match;
  const name = strings.versions.months[Number(month) - 1];
  return name === undefined ? day : `${String(Number(date))} ${name} ${year}`;
}

/**
 * Which build of that version this is: "1a2b3c4 · 2026-10-04 16:30", the commit
 * and when it was built, in lab time. Request of 4 October 2026 (second),
 * section 6.
 *
 * The same build id the update check compares with `/version.json`. It tells
 * two builds of one release apart, which is support's question and nobody
 * else's, so it is shown in the What's new sheet and no longer beside the
 * version.
 */
export function formatBuild(build: string = env.appVersion): string {
  const match = /^(?:([0-9a-f]{7,40})-)?(\d{8})T(\d{6})Z$/.exec(build);
  if (match === null) return build;
  const [, commit, day = '', time = ''] = match;
  const iso = `${day.slice(0, 4)}-${day.slice(4, 6)}-${day.slice(6, 8)}T${time.slice(0, 2)}:${time.slice(2, 4)}:${time.slice(4, 6)}Z`;
  return [commit, formatInstant(iso)].filter((part) => part !== undefined && part !== '').join(' · ');
}
